import React, { cloneElement, isValidElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LocateAddressSearch from './LocateAddressSearch';
import { formatFt } from '../../utils/unitsUs';

const STAGE_ROUTES = [
  { key: 'locate', label: 'Locate', kbd: 'L', to: '/xlabs/locate' },
  { key: 'design', label: 'Design', kbd: 'D', to: '/xlabs/design' },
  { key: 'analyse', label: 'Analyse', kbd: 'A', to: null },
  { key: 'build', label: 'Build', kbd: 'B', to: null },
];

/** Phone / short landscape — Pro Max landscape can be >900px wide. */
const COMPACT_MQ =
  '(max-width: 1100px), ((max-height: 520px) and (orientation: landscape))';
const PORTRAIT_PHONE_MQ = '(orientation: portrait) and (max-width: 520px)';

/**
 * Shared xlabs topbar / stage / status chrome for Locate + Design.
 * See docs/XLABS_LOCATE.md · docs/XLABS_DESIGN.md · docs/MOBILE_LANDSCAPE_UX.md
 */
export default function XlabsStageChrome({
  activeStage = 'locate',
  children,
  tool,
  ToolRail,
  SidePanel,
  sideProps,
  siteBooting,
  siteLoading,
  floorY,
  site,
  hudLeft = [],
  hudRight = [],
  northDeg = 0,
  showAddressSearch = false,
  bootName = 'xlabs',
  statusHint = '',
  modelLoadLabel = null,
  /** Compact sheet toggle label when closed (Design: "Models"). */
  panelToggleLabel = 'Controls',
  /** Auto-open the side sheet on compact enter (Design catalog). */
  autoOpenPanel = false,
  /** Open sheet when this tool id is active (Design: "place"). */
  openPanelOnTool = null,
  /** Floating canvas CTA when sheet closed (Design: "Models · Add"). */
  compactPanelCta = null,
  /** Close sheet after Add so the new model is visible (Design). */
  closePanelOnAdd = false,
  /** Close sheet after selecting a stacked structure (Design mobile). */
  closePanelOnSelect = false,
  /** Brand mark (default xlabs). Sim lab: same mark, different proj. */
  brandName = 'xlabs',
  /** Override projected stage label in brand (default: activeStage). */
  brandProj: brandProjProp,
  /** Brand link target (default hub). Sim lab → `/internal`. */
  brandTo = '/xlabs-hub',
  /** Brand back cue (default ← hub). */
  brandBack = '← hub',
  /**
   * Stage route tabs. `undefined` = Locate/Design defaults.
   * `null` or `[]` = hide routes (sim lab).
   */
  routes,
  /** Hide north compass (no site CRS — sim lab). */
  showNorthBadge = true,
  /** Pill label when not booting/loading (sim lab: "model ready"). */
  statusOverride = null,
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(COMPACT_MQ).matches,
  );
  const [addrOpen, setAddrOpen] = useState(false);
  const [showRotateCue, setShowRotateCue] = useState(false);
  const [rotateDismissed, setRotateDismissed] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(COMPACT_MQ);
    const onChange = () => {
      setCompact(mq.matches);
      if (!mq.matches) {
        setPanelOpen(false);
        setAddrOpen(false);
      } else if (autoOpenPanel) {
        setPanelOpen(true);
      }
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [autoOpenPanel]);

  useEffect(() => {
    if (compact && autoOpenPanel) setPanelOpen(true);
  }, [compact, autoOpenPanel, activeStage]);

  useEffect(() => {
    if (compact && openPanelOnTool && tool === openPanelOnTool) {
      setPanelOpen(true);
    }
  }, [compact, openPanelOnTool, tool]);

  useEffect(() => {
    if (!panelOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen || !compact) return undefined;
    const id = window.setTimeout(() => {
      document.getElementById('design-catalog')?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [panelOpen, compact, activeStage, tool]);

  useEffect(() => {
    const mq = window.matchMedia(PORTRAIT_PHONE_MQ);
    const onChange = () => {
      if (!rotateDismissed) setShowRotateCue(mq.matches);
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [rotateDismissed]);

  // After a site loads, collapse the floating search on compact screens.
  useEffect(() => {
    if (site && compact) setAddrOpen(false);
  }, [site, compact]);

  const statusLabel = siteBooting
    ? siteLoading
      ? 'SITE BOOT · geocoding'
      : 'SITE BOOT · floor lock'
    : modelLoadLabel
      ? `loading · ${modelLoadLabel}`
      : statusOverride != null
        ? statusOverride
        : site
          ? 'site ready'
          : 'idle';

  const pipClass =
    siteBooting || modelLoadLabel
      ? 'boot'
      : site || (statusOverride && statusOverride !== 'idle')
        ? 'ok'
        : '';
  const brandProj =
    brandProjProp ||
    (activeStage === 'design'
      ? 'Design'
      : activeStage.charAt(0).toUpperCase() + activeStage.slice(1));
  const stageRoutes = routes === undefined ? STAGE_ROUTES : routes || [];

  const mobileHint =
    compact && !statusHint
      ? `tap ${panelToggleLabel} for tools`
      : statusHint;

  const showCanvasAddr =
    showAddressSearch && (!compact || !site || addrOpen || !sideProps?.address);

  const addrChipLabel = (sideProps?.address || '').trim() || 'Search address…';

  const sidePanelNode = isValidElement(SidePanel)
    ? cloneElement(SidePanel, {
        ...(closePanelOnAdd
          ? {
              onAddStructure: (type) => {
                SidePanel.props.onAddStructure?.(type);
                if (compact) setPanelOpen(false);
              },
            }
          : null),
        ...(closePanelOnSelect
          ? {
              onSelectStructure: (id) => {
                SidePanel.props.onSelectStructure?.(id);
                if (compact) setPanelOpen(false);
              },
            }
          : null),
      })
    : SidePanel;

  return (
    <div
      className="app"
      data-screen-label={brandProj}
      data-panel-open={panelOpen ? 'true' : 'false'}
      data-compact={compact ? 'true' : 'false'}
      data-addr-open={addrOpen || showCanvasAddr ? 'true' : 'false'}
    >
      <header className="topbar">
        <Link className="brand" to={brandTo} title={`back to ${brandBack.replace(/^←\s*/, '')}`}>
          <span className="brand-name">{brandName}</span>
          <span className="brand-sep">·</span>
          <span className="brand-proj">{brandProj}</span>
          <span className="brand-back">{brandBack}</span>
        </Link>

        {stageRoutes.length > 0 ? (
          <nav className="routes" aria-label="Configurator stages">
            {stageRoutes.map((r) =>
              r.to ? (
                <Link
                  key={r.key}
                  className={`route${r.key === activeStage ? ' active' : ''}`}
                  to={r.to}
                >
                  <span className="route-label">{r.label}</span>
                  <span className="kbd">{r.kbd}</span>
                </Link>
              ) : (
                <span key={r.key} className="route muted" title="Coming soon">
                  <span className="route-label">{r.label}</span>
                  <span className="kbd">{r.kbd}</span>
                </span>
              ),
            )}
          </nav>
        ) : (
          <div className="routes" aria-hidden="true" />
        )}

        <div className="topright">
          {compact && (
            <button
              type="button"
              className={`mobile-panel-toggle${panelOpen ? ' open' : ''}`}
              aria-expanded={panelOpen}
              aria-controls="xlabs-side-panel"
              onClick={() => setPanelOpen((v) => !v)}
            >
              {panelOpen ? 'Close' : panelToggleLabel}
            </button>
          )}
          <div className="pill" title={statusLabel}>
            <span className={`spip ${pipClass}`} />
            <span className="pill-label">
              {statusLabel}
              {floorY != null && Number.isFinite(floorY) && !siteBooting
                ? ` · Y ${formatFt(floorY)}`
                : ''}
            </span>
          </div>
        </div>
      </header>

      <div className="stage">
        {ToolRail}

        <div className="canvas-pane">
          {children}

          {showAddressSearch && compact && site && !addrOpen && (
            <button
              type="button"
              className="addr-chip"
              onClick={() => setAddrOpen(true)}
              title="Change site address"
            >
              {addrChipLabel}
            </button>
          )}

          {showCanvasAddr && (
            <div className="addr-canvas-slot">
              {compact && site && (
                <button
                  type="button"
                  className="addr-dismiss"
                  aria-label="Collapse address search"
                  onClick={() => setAddrOpen(false)}
                >
                  ×
                </button>
              )}
              <LocateAddressSearch
                address={sideProps.address}
                onAddressChange={sideProps.onAddressChange}
                onLoadSite={(...args) => {
                  sideProps.onLoadSite(...args);
                  setAddrOpen(false);
                }}
                siteLoading={sideProps.siteLoading}
                siteError={sideProps.siteError}
                variant="canvas"
                autoFocus={!site || addrOpen}
              />
            </div>
          )}

          <div className="hud">
            {hudLeft.map((item) => (
              <div key={item.k}>
                <span className="h-k">{item.k}</span>{' '}
                <span className="h-v">{item.v}</span>
              </div>
            ))}
          </div>
          <div className="hud-r">
            {hudRight.map((item) => (
              <div key={item.k}>
                <span className="h-k">{item.k}</span>{' '}
                <span className="h-v">{item.v}</span>
              </div>
            ))}
          </div>
          {showNorthBadge ? (
            <div className="north-badge" aria-hidden>
              <span style={{ transform: `rotate(${-northDeg}deg)` }}>N</span>
            </div>
          ) : null}

          {compact && !panelOpen && compactPanelCta && (
            <button
              type="button"
              className="mobile-catalog-cta"
              onClick={() => setPanelOpen(true)}
            >
              {compactPanelCta}
            </button>
          )}

          {siteBooting && (
            <div className="boot">
              <div className="boot-card">
                <div className="boot-name">{bootName}</div>
                <div className="boot-msg">
                  {siteLoading
                    ? 'Loading site…'
                    : 'Locking Photorealistic floor…'}
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          id="xlabs-side-panel"
          className="side-slot"
          data-open={panelOpen && compact ? 'true' : 'false'}
        >
          {panelOpen && compact && (
            <button
              type="button"
              className="mobile-panel-backdrop"
              aria-label={`Close ${panelToggleLabel}`}
              onClick={() => setPanelOpen(false)}
            />
          )}
          {sidePanelNode}
        </div>
      </div>

      <footer className="statusbar">
        <span>
          tool <span className="ink">{tool}</span>
        </span>
        <span style={{ flex: 1 }} />
        <span>{mobileHint}</span>
      </footer>

      {showRotateCue && (
        <div className="rotate-cue" role="dialog" aria-label="Rotate device">
          <div className="rotate-cue-card">
            <p className="rotate-cue-title">Turn sideways</p>
            <p className="rotate-cue-sub">
              Site planner works best in landscape on phones.
            </p>
            <button
              type="button"
              className="rotate-cue-btn"
              onClick={() => {
                setRotateDismissed(true);
                setShowRotateCue(false);
              }}
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
